import React, { useState } from 'react';
import IconClose from '~icons/mdi/close';
import { ProblemStage, PageData, EvalData, noOp } from 'its-pages';
import { Dialog, DialogTitle } from '@mui/material';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import IconButton from '@mui/material/IconButton';
import { Centered } from '../common-components/centered';
import { styled as styledMui, useTheme } from '@mui/material'
import styled from '@emotion/styled';
import { pageCompFactory } from '../page-data/page-comp-factory';
import MobileStepper from '@mui/material/MobileStepper';
import KeyboardArrowLeft from '~icons/mdi/chevron-left';
import KeyboardArrowRight from '~icons/mdi/chevron-right';

const StyledDiv = styled("div")`
  comment: 'problem wrapper';
  padding: 1em;
  display: flex;
  flex-direction: column;
`;

const StyledTitle = styled("div")`
  font-size: 125%;
  font-weight: bold;
  user-select: none;
`;

const SimpleDialog = styledMui(Dialog)(({ theme }) => ({
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

function SimpleDialogTitle(props: DialogTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 1, pl: 2, 
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
     }} {...other}>
      {children}
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{ p: 0, color: (theme) => theme.palette.grey[500] }}
      >
        <IconClose />
      </IconButton>
    </DialogTitle>
  );
}

type StepperProps = {
  activeStep: number;
  maxSteps: number;
  onNext: VoidFunction | undefined;
  onPrev: VoidFunction | undefined;
}

export default function TextMobileStepper(props: StepperProps) {
  const theme = useTheme();
  const { activeStep, maxSteps, onNext, onPrev } = props;

  return (
      <MobileStepper
        variant="text"
        steps={maxSteps}
        position="static"
        activeStep={activeStep}
        nextButton={
          <Button
            size="small"
            onClick={onNext}
            disabled={activeStep === maxSteps - 1}
          >
            Next
            {theme.direction === 'rtl' ? (
              <KeyboardArrowLeft />
            ) : (
              <KeyboardArrowRight />
            )}
          </Button>
        }
        backButton={
          <Button size="small" onClick={onPrev} disabled={activeStep === 0}>
            {theme.direction === 'rtl' ? (
              <KeyboardArrowRight />
            ) : (
              <KeyboardArrowLeft />
            )}
            Back
          </Button>
        }
      />
  );
}

export interface Props {
  currentIdx?: number;
  maxIdx?: number;
  open: boolean;
  onClose: VoidFunction;
  onNewExample?: VoidFunction;
  /** If undefined, don't display anything */
  pageData?: PageData;
  active?: boolean;
  label?: string;
  onNext?: VoidFunction;
  onPrev?: VoidFunction;
  /** Defined if problem should only highlight parts related to given skill comps/tags */
  tagIdsToEmphasize?: string[];
}

/**
 * Component for the teacher to view either example questions or actual student responses to questions.
 * 
 * @param props 
 * @returns 
 */
export const StudentProblemDisplay = (props: Props) => {
  const { pageData, onClose, open, active, label, onNext, onPrev, onNewExample, tagIdsToEmphasize } = props;
  const [stage, setProblemStage] = useState(
    active ? ProblemStage.CollectInput : ProblemStage.ShowResult
  );

  if (!pageData) return null;
  const problemCompFromPageData = () => {
    const { type, playStats } = pageData;
    const CompType = pageCompFactory(type);
    const showSecondResponse = playStats.score < 1;
    const comp = CompType && (
      <CompType
        key={playStats.cumulativeTime}
        pageData={pageData as EvalData}
        stage={stage}
        showSecondResponse={showSecondResponse}
        isTeacherView={true}
        tagIdsToEmphasize={tagIdsToEmphasize}
      />
    );
    return comp;
  };

  const onShowResultsClick = () => {
    if (stage === ProblemStage.ShowResult) pageData.reset();
    setProblemStage(
      stage === ProblemStage.CollectInput
        ? ProblemStage.ShowResult
        : ProblemStage.CollectInput
    );
  };

  const showResultsButton = !active ? null : (
    <Centered>
      <Button onClick={onShowResultsClick}>
        {stage === ProblemStage.CollectInput ? 'Show Result' : 'Reset'}
      </Button>
    </Centered>
  );

  const title = label ? <StyledTitle>{label}</StyledTitle> : null;
  const hasNextPrev = (onNext !== undefined || onPrev !== undefined);
  const activeStep = (props.currentIdx ?? 0);
  const showBottomButtonUi = hasNextPrev || showResultsButton !== null || onNewExample

  return (
    <SimpleDialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      aria-labelledby='student-problem-display-title'
    >
      <SimpleDialogTitle id='student-problem-display-title' onClose={onClose}>
        {(title) ? title : `Example Problem`}
      </SimpleDialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Grid
          container
          justifyContent='flex-start'
          alignItems='center'
          direction='column'
        >
          <Grid item xs={11} p="1em" pb={0}>
            {problemCompFromPageData()}
          </Grid>
        </Grid>
      </DialogContent>
      {showBottomButtonUi  ? <DialogActions
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
      >
        {showResultsButton}
        {hasNextPrev ? <TextMobileStepper
          activeStep={activeStep}
          maxSteps={(props.maxIdx ?? 0) + 1}
          onNext={onNext}
          onPrev={onPrev}
        /> : null}
        {onNewExample ? <Centered>
          <Button onClick={onNewExample ?? noOp}>
            New Example
          </Button>
        </Centered> : null}
      </DialogActions> : null}
    </SimpleDialog>
  );
};
